import { Component, Prop, h, State, Build } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * App download banner component for SignNow brand
 * Shows only on mobile devices with platform-specific content
 *
 * @product signnow
 * @version 1.0.0
 * @category navigation
 * @contentConfig Mobile app download banner with platform detection
 * @constant APP_BANNER_TITLE - "Download signNow app" - Banner title
 * @constant APP_BANNER_RATING - "4.7" - App rating value
 * @constant APP_BANNER_RATING_TEXT - "/ 5 rating on" - Rating description text
 * @constant APP_BANNER_APP_STORE_TEXT - "App Store" - App Store text
 * @constant APP_BANNER_GOOGLE_PLAY_TEXT - "Google Play" - Google Play text
 * @constant LINK__APP_BANNER_APP_STORE - "https://itunes.apple.com/app/apple-store/id489262811?pt=482524&ct=main_site_footer&mt=8" - App Store download link
 * @constant LINK__APP_BANNER_GOOGLE_PLAY - "https://play.google.com/store/apps/details?id=com.signnow.android&referrer=utm_source%3Dmain_site_footer" - Google Play download link
 */
@Component({
  tag: 'sn-app-banner',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNAppBanner {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  @State() isIOS: boolean = false;
  @State() isAndroid: boolean = false;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);

    // Only run on browser
    if (Build.isBrowser) {
      const userAgent = window.navigator.userAgent.toLowerCase();
      this.isIOS = /iphone|ipad|ipod/.test(userAgent);
      this.isAndroid = /android/.test(userAgent);
    }
  }

  render() {
    // Reserve space on SSR or on desktop to prevent CLS (desktop block is hidden by CSS)
    const showReserve = !Build.isBrowser || (!this.isIOS && !this.isAndroid);
    if (showReserve) {
      return (
        <div class="app-banner">
          <div class="app-banner__row app-banner__row--reserve" aria-hidden="true" />
        </div>
      );
    }

    const downloadLink = this.isIOS
      ? getConstant(this.constants, 'LINK__APP_BANNER_APP_STORE', 'https://itunes.apple.com/app/apple-store/id489262811?pt=482524&ct=main_site_footer&mt=8')
      : getConstant(this.constants, 'LINK__APP_BANNER_GOOGLE_PLAY', 'https://play.google.com/store/apps/details?id=com.signnow.android&referrer=utm_source%3Dmain_site_footer');

    const storeText = this.isIOS
      ? getConstant(this.constants, 'APP_BANNER_APP_STORE_TEXT', 'App Store')
      : getConstant(this.constants, 'APP_BANNER_GOOGLE_PLAY_TEXT', 'Google Play');

    return (
      <div class="app-banner">
        <div class="app-banner__row">
          <div class="app-banner__column">
            <div class="app-banner__title">{getConstant(this.constants, 'APP_BANNER_TITLE', 'Download signNow app')}</div>
            <div class="app-banner__description">
              <span class="rating-accent">{getConstant(this.constants, 'APP_BANNER_RATING', '4.7')}</span> {getConstant(this.constants, 'APP_BANNER_RATING_TEXT', '/ 5 rating on')}{' '}
              <span class={`description-text ${this.isIOS ? 'description-text__app-store' : 'description-text__google-play'}`}>{storeText}</span>
            </div>
          </div>

          <div class="app-banner__column">
            <div class={`download-btn ${this.isIOS ? 'download-btn__app-store' : 'download-btn__google-play'}`}>
              <a href={downloadLink} title="Download signNow app" target="_blank" rel="noopener">
                <span class="download-btn-logo"></span>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
